import { defineStore, storeToRefs } from "pinia";
import { computed, ref, watchEffect } from "vue";
import {
  fetchTranscriptionsList as serviceFetchTranscriptionsList,
  fetchOneTranscription as serviceFetchOneTranscription,
} from "../services/apiTranscriptions";
import type {
  GetTranscriptionsResponse,
  GetTranscriptionZoomPayload,
  GetTranscriptionZoomResponse,
} from "@/commons/types/api/api.types";
import type { FetchTranscriptionsListParams } from "../services/apiTranscriptions";
import { useUserStore } from "./user";
import { useRoute } from "vue-router";
import { useTimeoutPoll } from "@vueuse/core";

const initialTranscriptionData = {
  transcriptionListLoading: false,
  itemCount: 0,
  transcriptions: [],
  transcriptionZoomLoading: false,
  transcriptionZoom: null,
};

const initialTranscriptionsFilters = {
  size: 20,
  from: 0,
  get_filters: true,
  source: null,
};

export const useTranscriptionsStore = defineStore("transcriptions", () => {
  const route = useRoute();
  const { sharedFiltersAsPayload } = storeToRefs(useUserStore());

  // Transcriptions filters
  const source = ref<string | null>(initialTranscriptionsFilters.source);

  // Additional filters
  const page = ref<number>(initialTranscriptionsFilters.from);
  const pageSize = ref<number>(initialTranscriptionsFilters.size);

  // Store GetTranscriptionsResponse data
  const transcriptionListLoading = ref(false);
  const itemCount = ref<GetTranscriptionsResponse["item_count"]>(0);
  const transcriptions = ref<GetTranscriptionsResponse["items"]>([]);
  const availableFilters = ref<GetTranscriptionsResponse["filters"] | null>(
    null
  );

  // Store GetTranscriptionZoomResponse data
  const transcriptionZoomLoading = ref(false);
  const transcriptionZoom = ref<
    GetTranscriptionZoomResponse["items"][0] | null
  >(null);

  const selectedTranscriptionId = computed(() => route.params.transcriptionId);

  const fetchSelectedTranscription = () =>
    fetchOneTranscription({
      _id: [selectedTranscriptionId.value as string],
      ...sharedFiltersAsPayload.value,
    });

  const { isActive, pause, resume } = useTimeoutPoll(
    fetchSelectedTranscription,
    60000,
    {
      immediate: false,
      immediateCallback: true,
    }
  );

  const resetZoom = () => {
    transcriptionZoomLoading.value =
      initialTranscriptionData.transcriptionZoomLoading;
    transcriptionZoom.value = initialTranscriptionData.transcriptionZoom;
  };
  const resetList = () => {
    transcriptionListLoading.value =
      initialTranscriptionData.transcriptionListLoading;
    itemCount.value = initialTranscriptionData.itemCount;
    transcriptions.value = initialTranscriptionData.transcriptions;
    source.value = initialTranscriptionsFilters.source;
    page.value = initialTranscriptionsFilters.from;
    pageSize.value = initialTranscriptionsFilters.size;
  };

  const reset = () => {
    resetZoom();
    resetList();
  };

  const fetchTranscriptionsList = async (
    payload: FetchTranscriptionsListParams
  ) => {
    transcriptionListLoading.value = true;
    try {
      const response: GetTranscriptionsResponse | undefined =
        await serviceFetchTranscriptionsList(payload);
      if (response) {
        transcriptions.value = response.items;
        itemCount.value = response.item_count;
        availableFilters.value = response.filters;
      }
    } catch {
    } finally {
      transcriptionListLoading.value = false;
    }
  };

  const fetchOneTranscription = async (
    payload: GetTranscriptionZoomPayload
  ) => {
    transcriptionZoomLoading.value = true;

    try {
      const response = await serviceFetchOneTranscription(payload);
      transcriptionZoom.value = response?.items?.[0] || null;
    } catch {
      throw new Error();
    } finally {
      transcriptionZoomLoading.value = false;
    }
  };

  return {
    transcriptions,
    transcriptionListLoading,
    startPolling: resume,
    stopPolling: pause,
    isPolling: isActive,
    itemCount,
    availableFilters,
    fetchTranscriptionsList,
    page,
    pageSize,
    selectedTranscriptionId,
    source,
    transcriptionZoomLoading,
    transcriptionZoom,
    reset,
    resetZoom,
    resetList,
  };
});

export const useAutoFetchTranscriptionList = () => {
  const { sharedFiltersAsPayload } = storeToRefs(useUserStore());
  const { pageSize, page, source } = storeToRefs(useTranscriptionsStore());
  const { fetchTranscriptionsList } = useTranscriptionsStore();

  watchEffect(() => {
    fetchTranscriptionsList({
      size: pageSize.value,
      from: page.value,
      source: source.value,
      get_filters: true,
      ...sharedFiltersAsPayload.value,
    });
  });
};
